import { t } from '@lingui/macro'
import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { PdfSplittableTableRow } from 'app/views/forms/pdf-components/PdfSplittableTableRow'
import { PdfTableCell } from 'app/views/forms/pdf-components/PdfTableCell'
import { PdfTableHeaderCell } from 'app/views/forms/pdf-components/PdfTableHeaderCell'
import { PdfTableHeaderConfig, pdfTableStyles } from 'app/views/forms/pdf-components/PdfTableStyles'
import { PdfUnsplittableTableRow } from 'app/views/forms/pdf-components/PdfUnsplittableTableRow'
import React from 'react'
import { myI18n } from 'translation/I18nConnectedProvider'

const styles = StyleSheet.create({
  objectiveCol: {
    width: '20%',
    fontWeight: 'bold'
  },
  // Needs column is optional, Outcomes take over its width when it's hidden
  needsCol: {
    width: '40%'
  },
  outcomesCol: {
    width: '40%'
  },
  outcomesColWithoutNeeds: {
    width: '80%'
  }
})

interface ObjectiveOption {
  value?: string
  labels?: Record<string, string>
}

interface ObjectiveValue {
  /** Key of the selected objective option */
  objective: string
  needs?: string
  outcomes?: string[]
}

interface FormObjectivesPdfTypeProps extends PdfTableHeaderConfig {
  /** Shows the Needs column. When hidden, Outcomes take over its width */
  displayNeedsInPDF?: boolean
  options?: ObjectiveOption[]
}

interface FormObjectivesPdfProps {
  title?: string
  langVersion: string
  typeProps: FormObjectivesPdfTypeProps
  value: ObjectiveValue[]
}

export const FormObjectivesPdf = ({
  title,
  langVersion,
  typeProps,
  value
}: FormObjectivesPdfProps): React.ReactElement => {
  const { displayNeedsInPDF, options = [] } = typeProps

  const headerProps = { headerConfig: typeProps }
  const outcomesColStyle = displayNeedsInPDF ? styles.outcomesCol : styles.outcomesColWithoutNeeds

  const objectiveToTranslation: Record<string, string> = {}
  options.forEach((opt) => {
    if (opt.labels && opt.value) {
      objectiveToTranslation[opt.value] = getLabelFromTranslationData({
        data: opt.labels,
        langVersion
      })
    }
  })

  return (
    <View style={pdfTableStyles.table}>
      {title ? (
        <View style={pdfTableStyles.tableCol}>
          <Text style={pdfTableStyles.title}>{title}</Text>
        </View>
      ) : (
        <View />
      )}
      <PdfUnsplittableTableRow>
        <PdfTableHeaderCell {...headerProps} style={styles.objectiveCol}>
          {myI18n?._(t`Objective`)}
        </PdfTableHeaderCell>
        {displayNeedsInPDF && (
          <PdfTableHeaderCell {...headerProps} style={styles.needsCol}>
            {myI18n?._(t`Needs`)}
          </PdfTableHeaderCell>
        )}
        <PdfTableHeaderCell {...headerProps} style={outcomesColStyle}>
          {myI18n?._(t`Outcomes`)}
        </PdfTableHeaderCell>
      </PdfUnsplittableTableRow>
      {value.map((data, index) => {
        const { objective, needs, outcomes = [] } = data
        return (
          <PdfSplittableTableRow key={index}>
            <PdfTableCell style={styles.objectiveCol}>
              <Text>{objectiveToTranslation[objective] || myI18n._(objective)}</Text>
            </PdfTableCell>
            {displayNeedsInPDF && (
              <PdfTableCell style={styles.needsCol}>
                <Text>{needs}</Text>
              </PdfTableCell>
            )}
            <PdfTableCell style={outcomesColStyle}>
              {outcomes.map((item, index) => (
                <View key={index}>
                  <Text>- {myI18n._(item)}</Text>
                </View>
              ))}
            </PdfTableCell>
          </PdfSplittableTableRow>
        )
      })}
    </View>
  )
}
